import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { format } from "date-fns";
import {
  SubscriptionPackage,
  SubscriptionPayment,
  cancelPayment,
  confirmDemoPayment,
  formatMoney,
  getPayment,
  startWhishPayment,
} from "../../services/subscriptionService";
import { downloadSubscriptionInvoice } from "../../utils/subscriptionInvoicePdf";

/** Name of the popup Whish opens in; the page it returns to closes itself. */
export const WHISH_WINDOW_NAME = "whish-payment";
export const WHISH_DONE_MESSAGE = "whish-payment-done";

const POLL_MS = 3000;

type Step = "confirm" | "phone" | "otp" | "waiting" | "paid";

interface Props {
  /** The package being paid for; null keeps the dialog closed. */
  pkg: SubscriptionPackage | null;
  organizationName: string;
  mode: "live" | "demo";
  /** Paid: the subscription has been extended. */
  onPaid: (payment: SubscriptionPayment) => void;
  /** A started payment ended unpaid (cancelled, declined or abandoned). */
  onAbandoned: () => void;
  /** Closed before any payment was started. */
  onDismiss: () => void;
  /** Leaves after a successful payment ("Back to dashboard"). */
  onDone: () => void;
}

const darkButton = {
  minHeight: 48,
  textTransform: "none",
  fontWeight: 700,
  bgcolor: "#111827",
  "&:hover": { bgcolor: "#1F2937" },
} as const;

/**
 * Paying for a package, in a window over the dashboard (blurred behind).
 *
 * Live: Whish's own page opens in a popup, where the payer enters their
 * number and confirms with the OTP in the Whish app; this window waits and
 * checks the payment until Whish confirms it. Demo (no Whish credentials on
 * the server): the same steps are simulated here and nothing is charged.
 *
 * Closing or cancelling before paying marks the payment failed.
 */
const WhishPaymentDialog: React.FC<Props> = ({
  pkg,
  organizationName,
  mode,
  onPaid,
  onAbandoned,
  onDismiss,
  onDone,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState<Step>("confirm");
  const [payment, setPayment] = useState<SubscriptionPayment | null>(null);
  const [phone, setPhone] = useState("+961 ");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const popup = useRef<Window | null>(null);
  const collectUrl = useRef<string | null>(null);

  // Fresh state each time a package is chosen.
  useEffect(() => {
    if (!pkg) return;
    setStep("confirm");
    setPayment(null);
    setPhone("+961 ");
    setOtp("");
    setError(null);
    setBusy(false);
  }, [pkg]);

  const settle = useCallback(
    (result: SubscriptionPayment) => {
      setPayment(result);
      if (result.status === "PAID") {
        setStep("paid");
        onPaid(result);
      } else if (result.status === "FAILED") {
        popup.current?.close();
        onAbandoned();
      }
    },
    [onPaid, onAbandoned]
  );

  // Live: check the payment until Whish confirms or declines it.
  useEffect(() => {
    if (step !== "waiting" || !payment) return;
    let stopped = false;
    let timer: number | undefined;
    const check = async () => {
      try {
        const latest = await getPayment(payment.id);
        if (stopped) return;
        if (latest.status !== "PENDING") return settle(latest);
      } catch {
        // Try again on the next tick.
      }
      if (!stopped) timer = window.setTimeout(check, POLL_MS);
    };
    // The popup's return page tells us straight away.
    const onMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data?.type === WHISH_DONE_MESSAGE) {
        window.clearTimeout(timer);
        check();
      }
    };
    window.addEventListener("message", onMessage);
    timer = window.setTimeout(check, POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
    };
  }, [step, payment, settle]);

  const begin = async () => {
    if (!pkg) return;
    setError(null);
    setBusy(true);
    // Opened inside the click, before any await, or the browser blocks it.
    if (mode === "live") {
      popup.current = window.open("", WHISH_WINDOW_NAME, "width=480,height=760");
    }
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const started = await startWhishPayment(pkg.id, returnUrl);
      setPayment(started.payment);
      if (started.mode === "demo" || !started.collectUrl) {
        setStep("phone");
      } else {
        collectUrl.current = started.collectUrl;
        if (popup.current && !popup.current.closed) {
          popup.current.location.href = started.collectUrl;
          setStep("waiting");
        } else {
          // Popups blocked: go to Whish in this tab; it brings the owner back.
          window.location.assign(started.collectUrl);
        }
      }
    } catch (err: any) {
      popup.current?.close();
      const message = err?.response?.data?.message;
      setError((Array.isArray(message) ? message[0] : message) || "Could not start the payment.");
    }
    setBusy(false);
  };

  const demoFinish = async (outcome: "success" | "failed") => {
    if (!payment) return;
    setBusy(true);
    setError(null);
    try {
      settle(await confirmDemoPayment(payment.id, outcome));
    } catch {
      setError("Could not reach the server. Please try again.");
    }
    setBusy(false);
  };

  // Closing before paying abandons the payment.
  const close = async () => {
    if (step === "paid") return onDone();
    popup.current?.close();
    if (!payment) return onDismiss();
    setBusy(true);
    try {
      const result = await cancelPayment(payment.id);
      // Whish may have confirmed it in the meantime.
      if (result.status === "PAID") return settle(result);
    } catch {
      // The server fails it on its own after 30 minutes anyway.
    }
    setBusy(false);
    onAbandoned();
  };

  const amount = pkg ? formatMoney(pkg.price, pkg.currency) : "";

  return (
    <Dialog
      open={Boolean(pkg)}
      onClose={() => !busy && close()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      slotProps={{
        backdrop: { sx: { backdropFilter: "blur(6px)", bgcolor: "rgba(15, 23, 42, 0.35)" } },
        paper: { sx: { borderRadius: fullScreen ? 0 : 4 } },
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 3.5 }, position: "relative" }}>
        <IconButton
          aria-label={step === "paid" ? "Close" : "Cancel payment"}
          onClick={close}
          disabled={busy}
          sx={{ position: "absolute", top: 10, right: 10 }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5, pr: 5 }}>
          <Box
            aria-hidden
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: "#111827",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            W
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem" }}>Pay with Whish</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }} noWrap>
              {pkg?.name} · {amount}
            </Typography>
          </Box>
        </Box>

        {mode === "demo" && step !== "paid" && (
          <Alert severity="warning" sx={{ mb: 2.5, fontSize: "0.82rem" }}>
            Demo mode: no money moves. Any number and any 6-digit code work.
          </Alert>
        )}

        {step === "confirm" && (
          <>
            <Typography sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.6 }}>
              {organizationName ? `${organizationName}: ` : ""}
              {pkg?.name} for {pkg?.durationDays} days. You'll enter your Whish number and confirm
              with the code sent to your Whish app.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              disabled={busy}
              onClick={begin}
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={darkButton}
            >
              Continue to pay {amount}
            </Button>
          </>
        )}

        {step === "phone" && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (phone.replace(/\D/g, "").length < 8) {
                setError("Enter the number of your Whish account");
                return;
              }
              setError(null);
              setStep("otp");
            }}
          >
            <TextField
              label="Whish phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputProps={{ inputMode: "tel", autoComplete: "tel" }}
              fullWidth
              autoFocus
            />
            <Button type="submit" variant="contained" disableElevation fullWidth sx={{ ...darkButton, mt: 2.5 }}>
              Send code to my Whish app
            </Button>
          </Box>
        )}

        {step === "otp" && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!/^\d{6}$/.test(otp)) {
                setError("Enter the 6-digit code");
                return;
              }
              demoFinish("success");
            }}
          >
            <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 2.5 }}>
              <PhoneIphoneIcon sx={{ color: "text.secondary" }} aria-hidden />
              <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                We sent a code to the Whish app on <strong>{phone.trim()}</strong>. Enter it to
                confirm {amount}.
              </Typography>
            </Box>
            <TextField
              label="Code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{
                inputMode: "numeric",
                autoComplete: "one-time-code",
                style: { letterSpacing: 6, fontSize: "1.2rem" },
              }}
              fullWidth
              autoFocus
            />
            <Button
              type="submit"
              variant="contained"
              disableElevation
              fullWidth
              disabled={busy}
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{ ...darkButton, mt: 2.5 }}
            >
              Confirm payment
            </Button>
          </Box>
        )}

        {step === "waiting" && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <CircularProgress size={34} sx={{ mb: 2 }} />
            <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Finish paying in the Whish window</Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 2.5 }}>
              Enter your Whish number there and confirm with the code in your Whish app. This
              window updates by itself.
            </Typography>
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                if (!collectUrl.current) return;
                popup.current = window.open(collectUrl.current, WHISH_WINDOW_NAME, "width=480,height=760");
              }}
              sx={{ textTransform: "none", minHeight: 44 }}
            >
              Reopen the Whish window
            </Button>
          </Box>
        )}

        {step === "paid" && payment && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 52, color: "#16A34A", mb: 1 }} aria-hidden />
            <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", mb: 0.75 }}>Payment received</Typography>
            <Typography sx={{ color: "text.secondary", mb: 2.5 }}>
              {payment.planName} is active
              {payment.periodEndsAt ? ` until ${format(new Date(payment.periodEndsAt), "d MMMM yyyy")}` : ""}.
              Invoice #{payment.invoiceNumber}.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.25, flexDirection: "column" }}>
              <Button variant="contained" disableElevation onClick={onDone} sx={{ ...darkButton }}>
                Back to dashboard
              </Button>
              <Button
                onClick={() => downloadSubscriptionInvoice(payment, organizationName)}
                sx={{ textTransform: "none", minHeight: 44 }}
              >
                Download invoice (PDF)
              </Button>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {step !== "paid" && step !== "confirm" && (
          <Button
            fullWidth
            disabled={busy}
            onClick={() => (mode === "demo" ? demoFinish("failed") : close())}
            sx={{ mt: 1.5, minHeight: 44, textTransform: "none", color: "text.secondary" }}
          >
            Cancel payment
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default WhishPaymentDialog;
